import { HttpApiEndpoint, HttpApiGroup } from "@effect/platform";
import * as S from "effect/Schema";
import { PagingParams, pagedResponse } from "./paging.js";
import { PrincipalMiddleware } from "./principal.js";
import { Forbidden } from "./shares.js";
/**
 * A `locations` row for one client — the admin client-config screen's
 * location list/editor, and the same table `employees.createEmployee`'s
 * `location_ids` and `shares.verifyUserShares`'s `location` type reference.
 */
export const LocationRow = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    name: S.String,
    key: S.NullOr(S.String),
    city: S.NullOr(S.String),
    country: S.NullOr(S.String),
    postal_code: S.NullOr(S.String),
    street: S.NullOr(S.String),
    street_number: S.NullOr(S.String),
    is_default: S.NullOr(S.Boolean),
    date_created: S.NullOr(S.String),
});
/**
 * A location plus its address in each external system it also lives in —
 * Omnitracker, which dispatches the inspections, and Fabiola.
 *
 * Deliberately a separate schema rather than two more fields on `LocationRow`:
 * that row is reused by contracts with a different audience (`employees.*`),
 * and PMO-158 scopes this integration to `ohs_admin`. Keeping the keys on the
 * admin shape alone means no unrelated endpoint can start returning them by
 * inheritance.
 *
 * Read-only: the hourly Omnitracker import writes them, and no write intent on
 * this group accepts them. Either may be null — a location need not exist in
 * both systems — and both read null for a caller who is not an admin.
 */
export const AdminLocationRow = S.extend(LocationRow, S.Struct({
    omnitracker_key: S.NullOr(S.String),
    fabiola_key: S.NullOr(S.String),
}));
/**
 * A location plus its risk-environment counts, from
 * `vw_locations_with_counts` — the admin client-config location list renders
 * the three as columns while the client's risk center is enabled. Only the
 * list read resolves them, so the write intents keep returning the bare
 * `LocationRow` rather than a count this contract has not read.
 */
export const LocationListRow = S.extend(AdminLocationRow, S.Struct({
    risk_areas_count: S.Int,
    risk_objects_count: S.Int,
    risk_activities_count: S.Int,
}));
export const ListLocationsParams = S.extend(PagingParams, S.Struct({
    client_id: S.UUID,
    /** Case-insensitive name filter — the licenses screen's location picker types ahead. */
    search: S.optional(S.NonEmptyString),
}));
export const ListLocations = HttpApiEndpoint.get("listLocations", "/admin/locations")
    .setUrlParams(ListLocationsParams)
    .addSuccess(pagedResponse(LocationListRow))
    .addError(Forbidden, { status: 403 });
export const CreateLocationPayload = S.Struct({
    client_id: S.UUID,
    name: S.NonEmptyString,
    key: S.optional(S.String),
    city: S.optional(S.String),
    country: S.optional(S.String),
    postal_code: S.optional(S.String),
    street: S.optional(S.String),
    street_number: S.optional(S.String),
    is_default: S.optional(S.Boolean),
});
export const CreateLocation = HttpApiEndpoint.post("createLocation", "/admin/locations/create")
    .setPayload(CreateLocationPayload)
    .addSuccess(S.Struct({ data: AdminLocationRow }))
    .addError(Forbidden, { status: 403 });
export const LocationNotFound = S.Struct({
    _tag: S.Literal("LocationNotFound"),
});
export const UpdateLocationPayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
    name: S.optional(S.NonEmptyString),
    key: S.optional(S.NullOr(S.String)),
    city: S.optional(S.NullOr(S.String)),
    country: S.optional(S.NullOr(S.String)),
    postal_code: S.optional(S.NullOr(S.String)),
    street: S.optional(S.NullOr(S.String)),
    street_number: S.optional(S.NullOr(S.String)),
    is_default: S.optional(S.Boolean),
});
export const UpdateLocation = HttpApiEndpoint.post("updateLocation", "/admin/locations/update")
    .setPayload(UpdateLocationPayload)
    .addSuccess(S.Struct({ data: AdminLocationRow }))
    .addError(LocationNotFound, { status: 404 })
    .addError(Forbidden, { status: 403 });
/** The location still has dependent rows (location_shares, risk/inspection references). */
export const LocationInUse = S.Struct({ _tag: S.Literal("LocationInUse") });
export const DeleteLocationPayload = S.Struct({
    id: S.UUID,
    client_id: S.UUID,
});
export const DeleteLocation = HttpApiEndpoint.post("deleteLocation", "/admin/locations/delete")
    .setPayload(DeleteLocationPayload)
    .addSuccess(S.Struct({ data: S.Struct({ deleted: S.Literal(true) }) }))
    .addError(LocationNotFound, { status: 404 })
    .addError(LocationInUse, { status: 409 })
    .addError(Forbidden, { status: 403 });
/**
 * The locations a given user already holds a `location_shares` row for,
 * within one client — the add-client licenses screen's row-click flow
 * (open a location-manager's edit modal pre-filled with their current
 * locations). Scoped by `client_id` the same way every other admin-locations
 * read is, rather than trusting the caller's `user_id` alone.
 */
export const ListUserLocationSharesParams = S.Struct({
    client_id: S.UUID,
    user_id: S.UUID,
});
export const ListUserLocationShares = HttpApiEndpoint.get("listUserLocationShares", "/admin/locations/user-shares")
    .setUrlParams(ListUserLocationSharesParams)
    .addSuccess(S.Struct({ items: S.Array(LocationRow), total: S.Int }))
    .addError(Forbidden, { status: 403 });
export const adminLocationsGroup = HttpApiGroup.make("admin-locations")
    .add(ListLocations)
    .add(CreateLocation)
    .add(UpdateLocation)
    .add(DeleteLocation)
    .add(ListUserLocationShares)
    .middleware(PrincipalMiddleware);
//# sourceMappingURL=admin-locations.js.map